import { useState } from "react";
import "./TemplatesPage.css";
import "../components/MessagingButtons.css";
import "../components/MessagingPage.css";
import "../components/ClientSelectableTable.css";
import { useAuth } from "../../../hooks/useAuth";
import { useMessageTemplates } from "../../../hooks/useMessageTemplates";
import {
  createMessageTemplateAsync,
  deleteMessageTemplateAsync,
  updateMessageTemplateAsync,
} from "../../../api/MessageTemplateApi";
import { Constants } from "../../../constants/constants";
import {
  MessageChannel,
  MessageTemplate,
  MessageType,
  MESSAGE_CHANNEL_LABELS,
  MESSAGE_TYPE_LABELS,
} from "../../../models/MessageModels";
import TemplateFormModal, {
  TemplateFormValues,
  TemplateModalMode,
} from "./TemplateFormModal";
import Toast from "../../../components/Toast";
import { OrgForms } from "../../../config/forms";

interface IconProps {
  title?: string;
}

const EditIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

const DeleteIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const TemplatesPage = () => {
  const { user, hasPrivilege } = useAuth();
  const organizationId = user?.activeOrganization?.organizationId;
  const canCreateTemplates = hasPrivilege(OrgForms.Messaging, "canCreate");
  const canEditTemplates = hasPrivilege(OrgForms.Messaging, "canEdit");
  const canDeleteTemplates = hasPrivilege(OrgForms.Messaging, "canDelete");

  const {
    templates,
    loading,
    error,
    reload,
    addLocal,
    updateLocal,
    removeLocal,
  } = useMessageTemplates(organizationId);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<TemplateModalMode>("create");
  const [selectedTemplate, setSelectedTemplate] =
    useState<MessageTemplate | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const showToast = (message: string, type: "success" | "error" | "info") =>
    setToast({ message, type });

  const openModal = (mode: TemplateModalMode, template: MessageTemplate | null) => {
    setModalMode(mode);
    setSelectedTemplate(template);
    setModalOpen(true);
  };

  const handleSubmit = async (form: TemplateFormValues) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      if (modalMode === "create") {
        if (!organizationId) {
          showToast("No active organization", "error");
          return;
        }
        const payload: MessageTemplate = {
          id: "",
          organizationId,
          name: form.name,
          channel: form.channel,
          messageType: form.messageType,
          body: form.body,
          isActive: form.isActive,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const res = await createMessageTemplateAsync(token, payload);
        if (res.success && res.result) {
          addLocal(res.result);
          setModalOpen(false);
          showToast("Template created", "success");
        } else {
          showToast(res.message || "Failed to create template", "error");
        }
        return;
      }

      if (modalMode === "edit" && selectedTemplate) {
        const res = await updateMessageTemplateAsync(token, selectedTemplate.id, {
          name: form.name,
          channel: form.channel,
          messageType: form.messageType,
          body: form.body,
          isActive: form.isActive,
        });
        if (res.success) {
          updateLocal({
            ...selectedTemplate,
            name: form.name,
            channel: form.channel,
            messageType: form.messageType,
            body: form.body,
            isActive: form.isActive,
          });
          setModalOpen(false);
          showToast("Template updated", "success");
        } else {
          showToast(res.message || "Failed to update template", "error");
        }
      }
    } catch (err) {
      console.error(err);
      showToast("Something went wrong. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (template: MessageTemplate) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      const res = await deleteMessageTemplateAsync(token, template.id);
      if (res.success) {
        removeLocal(template.id);
        showToast("Template deleted", "success");
      } else {
        showToast(res.message || "Failed to delete template", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to delete template. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return <div className="msg-table-empty">Loading templates…</div>;
    }

    if (error) {
      return (
        <div className="msg-table-empty">
          <p>{error}</p>
          <button className="msg-btn msg-btn--secondary" onClick={reload}>
            Retry
          </button>
        </div>
      );
    }

    if (templates.length === 0) {
      return (
        <div className="msg-table-empty">
          No templates yet. Click “New Template” to create one with placeholders
          like {"{ClientName}"}.
        </div>
      );
    }

    return (
      <div className="msg-table__wrap">
        <table className="msg-table__grid msg-templates-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Channel</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((template) => (
              <tr key={template.id}>
                <td className="msg-templates-table__name">
                  {template.name}
                  <span className="msg-templates-table__body">
                    {template.body}
                  </span>
                </td>
                <td>
                  {MESSAGE_TYPE_LABELS[template.messageType as MessageType] ?? "—"}
                </td>
                <td>
                  {MESSAGE_CHANNEL_LABELS[template.channel as MessageChannel] ?? "—"}
                </td>
                <td>
                  <span
                    className={`msg-templates-table__badge ${
                      template.isActive ? "active" : "inactive"
                    }`}
                  >
                    {template.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="msg-templates-table__actions">
                  {canEditTemplates && (
                    <button
                      className="msg-templates-table__icon msg-templates-table__icon--edit"
                      title="Edit"
                      aria-label={`Edit template "${template.name}"`}
                      onClick={() => openModal("edit", template)}
                    >
                      <EditIcon />
                    </button>
                  )}
                  {canDeleteTemplates && (
                    <button
                      className="msg-templates-table__icon msg-templates-table__icon--delete"
                      title="Delete"
                      aria-label={`Delete template "${template.name}"`}
                      onClick={() => {
                        if (window.confirm(`Delete template "${template.name}"?`)) {
                          handleDelete(template);
                        }
                      }}
                    >
                      <DeleteIcon />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="msg-page">
      <div className="msg-page__header">
        <div>
          <h2>Templates</h2>
          <p>
            Reusable message bodies for{" "}
            {user?.activeOrganization?.name ?? "your organization"}.
          </p>
        </div>
        {canCreateTemplates && (
          <button
            className="msg-btn msg-btn--primary"
            onClick={() => openModal("create", null)}
          >
            + New Template
          </button>
        )}
      </div>

      {renderBody()}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <TemplateFormModal
        isOpen={modalOpen}
        mode={modalMode}
        template={selectedTemplate}
        submitting={submitting}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default TemplatesPage;